<template>
  <UIDialog
    :is-open
    :z-index
    size="2md"
    title="Add view"
    @close="handleClose"
  >
    <div class="add-view-dialog">
      <AddDialogItem
        v-for="item in items"
        :key="item.name"
        :item
        @click="handleClickItem"
        @click:upgrade="handleClickUpgrade"
      />
      <AddDialogItem
        :item="{
          label: CUSTOM_VIEW_NAME,
          icon: DocumentIcon,
        }"
        @click="handleClickCustom"
      />
      <AddDialogItem
        v-if="isAIEnabled"
        :item="{
          label: t('Ask AI'),
          icon: AIIcon,
        }"
        variant="secondary"
        @click="handleClickAI"
      />
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useRouter } from 'vue-router'

import { AddDialogItem as TAddDialogItem } from './utils/types'
import { ModalClass, Widget } from '@types'
import { I18nTranslate } from '@/plugins/i18n/utils/types'

import { ROUTE_NAME } from '@/const'
import { CUSTOM_VIEW_NAME } from '../utils/const'
import { POSSIBLE_DASHBOARDS } from './utils/const'

import { useSettingsStore } from '@/store/settings'
import { useRepositoriesStore } from '@/store/repositories'

import AddDialogItem from './AddDialogItem.vue'
import { UIDialog } from '@ui/modals'
import {
  Bars4Icon,
  CalendarDaysIcon,
  ChartPieIcon,
  DocumentIcon,
  RectangleGroupIcon,
  ScaleIcon,
  TableCellsIcon,
} from '@heroicons/vue/24/outline'
import AIIcon from '@/assets/images/icons/ai.svg'

type Props = {
  modal: ModalClass<any>
}

type Emits = {
  click: [data: Widget[], name: string, slug?: string, useAI?: boolean]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const repositoriesStore = useRepositoriesStore()
const settingsStore = useSettingsStore()

const router = useRouter()

const t = inject<I18nTranslate>('t', (data: string) => data)

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const isAIEnabled = computed(() => settingsStore.getAIAgent)

const availableWidgets = computed(() => repositoriesStore.getAvailableWidgets)

const getDashboards = computed(() => POSSIBLE_DASHBOARDS)

const items = computed(() =>
  getDashboards.value?.map(item => {
    let icon
    switch (item.slug) {
      case 'portfolio':
        icon = ChartPieIcon
        break
      case 'value-pivot':
      case 'tree-pivot':
        icon = RectangleGroupIcon
        break
      case 'commitments':
        icon = TableCellsIcon
        break
      case 'linked-transactions':
        icon = Bars4Icon
        break
      case 'assets-liabilities':
        icon = ScaleIcon
        break
      case 'cash-flow-projections':
        icon = CalendarDaysIcon
        break
    }
    return {
      label: item.name,
      name: item.slug,
      icon,
      // TODO: figure out how to check the rights of the plan not so strictly
      unavailable:
        item.slug === 'commitments' && !availableWidgets.value.includes(item.name),
    }
  }),
)

const handleClose = () => {
  props.modal.close()
}

const handleClickItem = (data: TAddDialogItem) => {
  props.modal.close()
  const widgets = getDashboards.value.find(
    item => item.slug === data.name,
  )?.widgets
  emit('click', widgets || [], data.label, data.name, data.useAI)
}

const handleClickCustom = () => {
  handleClickItem({
    label: CUSTOM_VIEW_NAME,
  })
}

const handleClickAI = () => {
  handleClickItem({
    label: CUSTOM_VIEW_NAME,
    useAI: true,
  })
}

const handleClickUpgrade = () => {
  handleClose()
  router.push({ name: ROUTE_NAME.SUBSCRIPTION })
}
</script>

<style>
.add-view-dialog {
  @apply grid grid-cols-2 md:grid-cols-3;
  @apply gap-6 p-4;
}
</style>
